import React from "react";
import { useTranslation } from "react-i18next";
import {
    $Button,
    $Input,
    $Row,
    $Col,
    $Skeleton,
    $Popover,
    $SelectGroup,
    PostalCodeInput,
} from "us.common/components";
import { ExclamationCircleFilled } from "us.icons";
import _ from "lodash";
import { DEFAULT_COUNTRY_CODE, europeanCountry } from "us.common/constants";


type AddressInfoProps = {
    isFetching: boolean;
    isSaving: boolean;
    postalcode?: {
        data: Array<any>;
        isLoading: boolean;
        isResponse: boolean;
    };
    changeView: (rest: any) => void;
    searchPostalcode: any;
    searchMunicipilityDetail: (code: string, values: any, rest: any) => void;
    onChangeCountry: (value: any, rest: any, values: any) => void;
    countryPopOverConfirmYes: (rest: any, values: any) => void;
    countryPopOverConfirmNo: (rest: any) => void;
    values: any;
    rest: any;
};
/**
 * @description
 * AddressInfo Component
 * --------------------------
 * This component is responsible for rendering and managing
 * address-related form inputs such as:
 *  - Country selection
 *  - Address lines
 *  - Postal code & area
 *  - Municipality details
 *
 * It also handles:
 *  - Postal code lookup
 *  - Municipality lookup
 *  - Country change confirmation
 */

const AddressInfo: React.FC<AddressInfoProps> = (props) => {
    const { t } = useTranslation();
    const {
        values,
        rest,
        isFetching,
        isSaving,
        countryPopOverConfirmNo,
        countryPopOverConfirmYes,
        onChangeCountry,
        changeView,
        postalcode,
        searchMunicipilityDetail,
        searchPostalcode
    } = props;

    return (
        <div className='pl-1 pb-4'>
            <$Skeleton
                loading={
                    isFetching ||
                    isSaving
                }>
                <$Row
                    gutter={
                        16
                    }>
                    <$Col
                        span={
                            6
                        }>
                        <$Popover
                            placement='rightTop'
                            content={
                                <div
                                    style={{
                                        maxWidth: '300px',
                                    }}>
                                    <div className='ant-popover-message'>
                                        <ExclamationCircleFilled />
                                        <div className='ant-popover-message-title'>
                                            <div
                                                style={{
                                                    paddingBottom:
                                                        '0.3rem',
                                                }}>
                                                {t(
                                                    'US.COLLECTION.DEBTOR:GENERAL.CHANGING_THE_COUNTRY_WILL_REMOVE_POSTAL_AND_MUNICIPALITY_DETAILS'
                                                )}
                                            </div>
                                            <div
                                                style={{
                                                    paddingBottom:
                                                        '0.5rem',
                                                }}>
                                                <strong>
                                                    {t(
                                                        'US.COLLECTION.DEBTOR:GENERAL.DO_YOU_WANT_TO_CONTINUE'
                                                    )}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='ant-popover-buttons'>
                                        <$Button
                                            size='small'
                                            onClick={() =>
                                                countryPopOverConfirmNo(
                                                    rest
                                                )
                                            }
                                            data-testid='debtor-general-countryChangeConfirmNo'>
                                            {t(
                                                'US.COMMON:COMMON.NO'
                                            )}
                                        </$Button>
                                        <$Button
                                            size='small'
                                            type='primary'
                                            onClick={() =>
                                                countryPopOverConfirmYes(
                                                    rest,
                                                    values
                                                )
                                            }
                                            data-testid='debtor-general-countryChangeConfirmYes'>
                                            {t(
                                                'US.COMMON:COMMON.YES'
                                            )}
                                        </$Button>
                                    </div>
                                </div>
                            }
                            trigger='click'
                            visible={
                                values.popConfirmVisible
                            }>
                            <$SelectGroup
                                className='country-select'
                                required
                                options={_.orderBy(
                                    europeanCountry,
                                    [
                                        'name',
                                        'label',
                                    ],
                                    [
                                        'asc',
                                        'asc',
                                    ]
                                )}
                                formitem={{
                                    label: t(
                                        'US.COLLECTION.DEBTOR:GENERAL.COUNTRY'
                                    ),
                                }}
                                style={{
                                    width: '100%',
                                }}
                                value={
                                    values.countryCode
                                }
                                name='countryCode'
                                defaultValue={
                                    values.countryCode
                                }
                                optionFilterProp='children'
                                onChange={(
                                    value: any
                                ) =>
                                    onChangeCountry(
                                        value,
                                        rest,
                                        values
                                    )
                                }
                                filterOption={(
                                    input: any,
                                    option: any
                                ) =>
                                    option.children
                                        ?.toLowerCase()
                                        .indexOf(
                                            input.toLowerCase()
                                        ) >=
                                    0
                                }
                                data-testid='debtor-general-country'
                            />
                        </$Popover>
                    </$Col>
                </$Row>
                <$Row
                    gutter={
                        16
                    }
                    className='mt-2'>
                    <$Col
                        span={
                            8
                        }>
                        <$Input
                            name='address1'
                            value={
                                values.address1
                            }
                            label={t(
                                'US.COLLECTION.DEBTOR:GENERAL.POSTADDRESS1'
                            )}
                            size='small'
                            data-testid='debtor-general-address1'
                        />
                    </$Col>
                    <$Col
                        span={
                            8
                        }>
                        <$Input
                            name='address2'
                            value={
                                values.address2
                            }
                            label={t(
                                'US.COLLECTION.DEBTOR:GENERAL.POSTADDRESS2'
                            )}
                            size='small'
                            data-testid='debtor-general-address2'
                        />
                    </$Col>
                    <$Col
                        span={
                            8
                        }>
                        <$Input
                            name='address3'
                            value={
                                values.address3
                            }
                            label={t(
                                'US.COLLECTION.DEBTOR:GENERAL.POSTADDRESS3'
                            )}
                            size='small'
                            data-testid='debtor-general-address3'
                        />
                    </$Col>
                </$Row>
                <$Row
                    gutter={
                        16
                    }
                    className='mt-2'>
                    <$Col
                        span={
                            6
                        }>
                        <a
                            className='d-block'
                            style={{
                                paddingBottom:
                                    '0.13rem',
                            }}
                            onClick={() =>
                                changeView(
                                    rest
                                )
                            }>
                            <strong>
                                {t(
                                    'US.COMMON:COMMON.POSTAL_CODE'
                                )}
                            </strong>
                        </a>
                        <PostalCodeInput
                            name="zipCode"
                            size="small"
                            value={values.zipcode}
                            countryCodeField="countryCode"
                            defaultCountryCode={DEFAULT_COUNTRY_CODE}
                            useValueInput={true}
                            onSearch={({ countryCode, searchText, formData }) =>
                                searchPostalcode?.({
                                    countryCode,
                                    searchText,
                                    formData,
                                })
                            }
                            onValueChange={(value, { setValues, values }) => {
                                setValues({
                                    ...values,
                                    zipCode: value,
                                    zipName: "",
                                    municipalityName: "",
                                    municipalityCode: "",
                                });
                            }}

                        />
                    </$Col>
                    <$Col
                        span={
                            6
                        }>
                        <$Skeleton
                            loading={
                                postalcode?.isLoading
                            }
                            active
                            paragraph={{
                                rows: 0,
                            }}>
                            <$Input
                                name='zipName'
                                label={t(
                                    'US.COMMON:COMMON.POSTAL_AREA'
                                )}
                                size='small'
                                value={
                                    values.zipName
                                }
                                data-testid='debtor-general-zipName'
                                disabled={
                                    values.isExistingPostalCode
                                }
                            />
                        </$Skeleton>
                    </$Col>
                    <$Col
                        span={
                            6
                        }>
                        <$Input
                            name='municipalityCode'
                            label={t(
                                'US.COMMON:COMMON.MUNICIPALITY_CODE'
                            )}
                            size='small'
                            value={
                                values.municipalityCode
                            }
                            onBlur={(
                                data: any
                            ) =>
                                searchMunicipilityDetail(
                                    data
                                        ?.target
                                        ?.value,
                                    values,
                                    rest
                                )
                            }
                            data-testid='debtor-general-municipalityCode'
                            disabled={
                                values.isExistingPostalCode
                            }
                        />
                    </$Col>
                    <$Col
                        span={
                            6
                        }>
                        <$Input
                            name='municipalityName'
                            label={t(
                                'US.COMMON:COMMON.MUNICIPALITY_NAME'
                            )}
                            size='small'
                            value={
                                values.municipalityName
                            }
                            data-testid='debtor-general-municipalityName'
                            disabled={
                                values.isExistingMunicipalityCode
                            }
                        />
                    </$Col>
                </$Row>
            </$Skeleton>
        </div>
    );
};

export default AddressInfo;
